import { InsuranceVerificationStatus } from '@/types/insuranceVerification';

export enum ExternalVerificationSource {
  FRONTRUNNER = 'FRONTRUNNER',
  STEDI = 'STEDI'
}

export enum ExternalVerificationState {
  ERROR = 'ERROR',
  PENDING = 'PENDING',
  // No meaningful data returned
  REJECTED = 'REJECTED',
  SUCCESS = 'SUCCESS'
}

interface ExternalRejection {
  id: string;
  source: ExternalVerificationSource | null;
  description: string | null;
}

interface ExternalPolicyFinance {
  deductible_in_cents: number | null;
  deductible_remaining_in_cents: number | null;
  office_copay_in_cents: number | null;
  hospital_copay_in_cents: number | null;
  specialist_copay_in_cents: number | null;
  other_copay_in_cents: number | null;
  coinsurance_percentage: number | null;
  // deprecate coinsurance_in_cents
  coinsurance_in_cents: number | null;
  out_of_pocket_max_in_cents: number | null;
  out_of_pocket_remaining_in_cents: number | null;
  out_of_network_deductible_in_cents: number | null;
  out_of_network_deductible_remaining_in_cents: number | null;
  out_of_network_out_of_pocket_max_in_cents: number | null;
  out_of_network_out_of_pocket_remaining_in_cents: number | null;
  out_of_network_co_insurance_in_cents: number | null;
}

interface ExternalPolicyEligibility {
  coordination_of_benefits_order: string | null;
  eligibility_271: string | null;
  eligibility_status_code: string | null;
  eligibility_status_description: string | null;
  is_hospice_care: boolean;
  is_medicare_part_a: boolean;
  is_medicare_part_b: boolean;
  medicare_part_a_effective_on: string | null;
  medicare_part_a_terminate_on: string | null;
  medicare_part_b_effective_on: string | null;
  medicare_part_b_terminate_on: string | null;
  insurance_verification_status: InsuranceVerificationStatus;
}

export interface ExternalPolicyCoverage {
  eligibility_status_description?: string | null;
  plan_name: string | null;
  plan_description: string | null;
  certificate_number?: string | null;
  insurance_type: string | null;
  effective_on?: string | null;
  terminate_on?: string | null;
  services: string[] | null;
  message: string | null;
}

interface InsurancePolicyFromEPV {
  carrier: string | null;
  insurance_provider_id: string | null;
  insurance_provider_internal_name: string | null;
  plan_end_date: string | null;
  plan_start_date: string | null;
  plan: string | null;
  policyholder_dob: string | null;
  policyholder_first_name: string | null;
  policyholder_last_name: string | null;
  relationship: string | null;
  subscriber_id: string | null;
  type: string | null;
}

export interface ExternalPolicyVerification {
  id: string;
  effective_on: string | null;
  terminate_on: string | null;
  external_name: string | null;
  external_trace_id: string | null;
  external_vendor_id: string | null;
  rejections: ExternalRejection[] | null;
  finance: ExternalPolicyFinance | null;
  eligibility: ExternalPolicyEligibility | null;
  coverages: ExternalPolicyCoverage[] | null;
  subscriber_id: string | null;
  vendor: string | null;
  patient_insurance_id: string | null;
  pcp_first_name: string | null;
  pcp_id: string | null;
  pcp_last_name: string | null;
  plan_type_description: string | null;
  plan_type_id: string | null;
  // insurance verification id that was created from this policy verification
  policy_verification: string | null;
  benefit_inquiry_id: string | null;
  group_name: string | null;
  group_number: string | null;
  service_code: string | null;
  potential_insurance_policy_attrs: InsurancePolicyFromEPV | null;
  raw_data: {
    request_data: Record<string, unknown> | null;
    response_data: Record<string, unknown> | null;
  } | null;
  source: ExternalVerificationSource | null;
}
export interface ExternalVerification {
  id: string;
  policy_verifications: ExternalPolicyVerification[];
  discovered_policy_verifications: ExternalPolicyVerification[];
  response_data: Record<string, unknown>;
  rejections: ExternalRejection[];
  state: ExternalVerificationState;
  service_code: string | null;
  requested_at?: string | null;
  source?: ExternalVerificationSource | null;
}

export interface CheckVOB {
  flags?: {
    [key: string]: boolean;
  };
  vendor?: ExternalVerificationSource;
  service_code?: string;
}

// NOTE: We may have this mirror CheckVOB eventually
export interface AutoReverifyPayload {
  flags: {
    include_ssn: boolean;
  };
  vendor?: ExternalVerificationSource;
  service_code?: string;
}

export interface BulkAutoReverifyPayload {
  policy_ids: string[];
  vendor?: ExternalVerificationSource;
}

export interface CreateInsuranceVerificationRequest {
  external_policy_verification_id: string;
  mutable_fields?: MutableFieldsForInsuranceVerification | null;
}

export interface CreateInsuranceFromDiscoveredRequest {
  external_policy_verification_id: string;
  insurance_policy_to_end_id?: string | null;
  end_date?: string | null;
  insurance_policy_priority_status?: string | null;
  mutable_fields?: MutableFieldsForInsuranceVerification | null;
}

export interface MutableFieldsForInsuranceVerification {
  // Fields that the BE does not control (yet)
  vob_notes?: string | null;
  estimated_monthly_cost_in_cents?: number | null;
  estimated_initial_month_cost_in_cents?: number | null;
}
